"use client";

import { useState } from "react";
import { useMutation } from "react-query";
import { deleteProfile, requestDeleteProfile } from "../../api/user";
import { showErrorToast } from "../../utils/toast";

export default function DeleteAccountPage() {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");

  const mutation = useMutation(requestDeleteProfile, {
    onSuccess: () => {
        setStep(2);
    },
    onError: (error) => {
        showErrorToast(error.message || "Something went wrong");
    }
  });

  const deleteMutation = useMutation(deleteProfile, {
    onSuccess: () => {
        setStep(4);
    },
    onError: (error) => {
        showErrorToast(error.message || "Something went wrong");
    }
  });

  const sendOtp = async () => {
    if (!email) return setError("Enter a valid email");
    mutation.mutate({ email });
  };  

  const deleteAccount = async () => {
    deleteMutation.mutate({ email, otp });
  };

  return (
    <div className="min-h-screen bg-[#1B0A4B] text-white relative overflow-hidden">

      {/* subtle pattern */}
      <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle,_#00EFC1_1px,_transparent_1px)] [background-size:24px_24px]" />

      {/* HERO */}
      <div className="relative z-10 text-center px-6 pt-20 pb-12">
        <h1 className="text-4xl font-bold mb-4">
          Delete Your Jamestown Account
        </h1>
        <p className="text-sm text-gray-300 max-w-xl mx-auto">
          We’re sorry to see you go. Deleting your account is permanent and cannot be undone.
        </p>
      </div>

      {/* MAIN CARD */}
      <div className="relative z-10 max-w-3xl mx-auto px-6 pb-16">
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 space-y-10 shadow-2xl">

          {/* STEP INDICATOR */}
          <div className="flex justify-between text-xs">
            {["Email", "OTP & Confirmation", "Done"].map((s, i) => (
              <div
                key={i}
                className={`flex-1 text-center ${
                  step >= i + 1 ? "text-[#00EFC1]" : "text-gray-500"
                }`}
              >
                {s}
              </div>
            ))}
          </div>

          {/* CONTENT */}
          <div className="space-y-8 text-sm text-gray-300">

            <section>
              <h2 className="text-white font-semibold mb-2">
                Verify your request
              </h2>
              <ul className="list-disc pl-5 space-y-1">
                <li>Enter the email address linked to your Jamestown account</li>
                <li>We’ll send a One-Time Password (OTP) to that email</li>
                <li>Enter the OTP to proceed with deletion</li>
              </ul>
            </section>

            <section>
              <h2 className="text-white font-semibold mb-2">
                What happens when you delete your account?
              </h2>
              <ul className="list-disc pl-5 space-y-1">
                <li>Your profile and personal data will be permanently removed</li>
                <li>You will lose access to your account and all services</li>
                <li>Transaction history will no longer be accessible</li>
                <li>This action cannot be reversed</li>
              </ul>
            </section>

            <section>
              <h2 className="text-white font-semibold mb-2">
                Before you proceed
              </h2>
              <ul className="list-disc pl-5 space-y-1">
                <li>You have withdrawn all funds from your account</li>
                <li>There are no pending transactions</li>
                <li>You have saved any important information</li>
              </ul>
            </section>

            <section>
              <h2 className="text-white font-semibold mb-2">
                Steps to delete your account
              </h2>
              <ol className="list-decimal pl-5 space-y-1">
                <li>Enter your registered email address</li>
                <li>Click “Send OTP”</li>
                <li>Enter the OTP sent to your email</li>
                <li>Click “Verify & Continue”</li>
                <li>Confirm deletion</li>
              </ol>
            </section>

            <section className="bg-red-500/10 border border-red-500/30 p-4 rounded-lg">
              <h2 className="text-red-400 font-semibold mb-2">⚠️ Important</h2>
              <ul className="list-disc pl-5 space-y-1 text-red-200">
                <li>Account deletion is permanent and irreversible</li>
                <li>OTPs expire after a short time</li>
                <li>Some information may be retained for compliance</li>
                <li>Deletion may take up to 7 days to complete</li>
              </ul>
            </section>
          </div>

          {/* FORM */}
          <div className="space-y-4">

            {error && (
              <div className="bg-red-500/20 text-red-300 p-2 rounded text-sm">
                {error}
              </div>
            )}

            {step === 1 && (
              <>
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 outline-none focus:border-[#00EFC1]"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />

                <button
                  onClick={sendOtp}
                  disabled={mutation.isLoading}
                  className="w-full bg-[#00EFC1] text-black font-medium py-2 rounded-lg hover:opacity-90 disabled:opacity-50"
                >
                  {mutation.isLoading ? "Sending..." : "Send OTP"}
                </button>
              </>
            )}

            {step === 2 && (
              <>
                <div className="bg-red-500/20 border border-red-500/30 p-3 rounded text-sm text-red-300">
                  You are about to permanently delete your account.
                </div>

                <input
                  type="text"
                  placeholder="Enter OTP"
                  className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 outline-none focus:border-[#00EFC1]"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                />

                <button
                  onClick={deleteAccount}
                  disabled={deleteMutation.isLoading}
                  className="w-full bg-red-600 py-2 rounded-lg disabled:opacity-50"
                >
                  {deleteMutation.isLoading ? "Deleting..." : "Confirm Deletion"}
                </button>

                <button
                  onClick={sendOtp}
                  disabled={deleteMutation.isLoading}
                  className="text-xs text-[#00EFC1]"
                >
                  {mutation.isLoading ? `Resending OTP...` : "Resend OTP"}
                </button>
              </>
            )}

            {step === 4 && (
              <div className="text-center text-[#00EFC1] font-medium">
                Your deletion request has been submitted successfully.
              </div>
            )}
          </div>

          {/* HELP */}
          <div className="text-center text-xs text-gray-400">
            Need help instead?{" "}
            <a
              href="mailto:help@jamestown.ng"
              className="text-[#00EFC1]"
            >
              help@jamestown.ng
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}